import type { ReactElement } from "react";

import { useDispatch, useSelector } from "react-redux";

import type { BaseActivatedPanelProps } from "app/pages/sales-intelligence/common-components/table-actions-panel";
import { selectCountriesFilterValue } from "app/pages/sales-intelligence/modules/leads-search-filters";
import CRMContactsExportPanelContainer from "app/pages/sales-intelligence/sub-modules/contacts-search/containers/CRMContactsExportPanel";
import { useContactsFlowStrategy } from "app/pages/sales-intelligence/sub-modules/contacts-search/context/ContactsFlowContext";
import useActivePanelLifeCycle from "app/pages/sales-intelligence/sub-modules/contacts-search/hooks/useActivePanelLifeCycle";
import {
    useContactSearchResult,
    useInvalidateContactSearchResult,
} from "app/pages/sales-intelligence/sub-modules/contacts-search/hooks/useContactSearchResult";
import { exportSelectedContactsToExcelForLeadsSearchThunk } from "app/pages/sales-intelligence/sub-modules/contacts-search/store/effects";
import type { ContactSearchEntry } from "app/pages/sales-intelligence/sub-modules/contacts-search/types/common";
import type { ExportToExcelFn } from "app/pages/sales-intelligence/sub-modules/contacts-search/types/exportFlow";
import { WORLDWIDE_COUNTRY_ID } from "app/services/countryServiceConst";

import { fetchTopNContactsThunk } from "../../../store/effects";

const ContactsExportPanelContainer = (props: BaseActivatedPanelProps): ReactElement => {
    const dispatch = useDispatch();
    const contactsFlowStrategy = useContactsFlowStrategy();

    const { data, isFetching } = useContactSearchResult();
    const invalidateContacts = useInvalidateContactSearchResult();

    const countriesFilterValue = useSelector(selectCountriesFilterValue);
    const countries = countriesFilterValue ? countriesFilterValue : [WORLDWIDE_COUNTRY_ID];
    useActivePanelLifeCycle();

    const fetchTopNContacts = (n: number): Promise<ContactSearchEntry[]> => dispatch(fetchTopNContactsThunk(n));

    const handleExcelExportSubmit: ExportToExcelFn = async (topNOrPairs, isSilent) =>
        dispatch(
            exportSelectedContactsToExcelForLeadsSearchThunk(
                topNOrPairs,
                contactsFlowStrategy.dataSource.exportExcel,
                isSilent,
            ),
        );

    return (
        <CRMContactsExportPanelContainer
            contacts={data?.rows || []}
            totalCount={data?.totalCount || 0}
            isLoading={isFetching}
            countries={countries}
            onDeactivate={props.onDeactivate}
            onSuccess={invalidateContacts}
            onExportToExcel={handleExcelExportSubmit}
            onFetchTopNContacts={fetchTopNContacts}
        />
    );
};

export default ContactsExportPanelContainer;
